import { computed, signal } from '@angular/core';
import { FlashMapService } from '../services/flash-map.service';
import { FlashMapOptions, MindMapData } from '../models/types';
import {
  fromOutlineText,
  NODE_ID_PREFIX,
  OutlineParseError,
  OutlineTreeNode,
  toOutlineDocument,
  toOutlineTreeNodes,
} from './outline-adapter';

type OptionsSignal = () => Partial<FlashMapOptions>;
type EditableSignal = () => boolean;
type MetaProvider = () => MindMapData['meta'] | undefined;

export class OutlineCoordinator {
  readonly mode = signal<'map' | 'outline'>('map');
  readonly outlineText = signal('');
  readonly outlineError = signal<OutlineParseError | null>(null);
  readonly selectedOutlineLine = signal<string | null>(null);
  readonly editingOutlineNodeId = signal<string | null>(null);
  readonly outlineEnabled = computed(() => this.options().enableOutlineMode ?? true);

  private readonly outlineTreeNodesInternal = signal<OutlineTreeNode[]>([]);
  readonly outlineTreeNodes = computed(() => this.outlineTreeNodesInternal());

  private outlineLineToNodeId: Record<number, string> = {};
  private outlineNodeIdToLine: Record<string, number> = {};
  private outlineApplyTimer: number | null = null;
  private outlineRefreshTimer: number | null = null;

  constructor(
    private readonly flashMapService: FlashMapService,
    private readonly options: OptionsSignal,
    private readonly editable: EditableSignal,
    private readonly getCurrentMeta: MetaProvider
  ) {}

  toggleMode(): void {
    if (!this.outlineEnabled()) {
      return;
    }
    const nextMode = this.mode() === 'map' ? 'outline' : 'map';
    this.setMode(nextMode);
  }

  setMode(mode: 'map' | 'outline'): void {
    if (mode === this.mode()) {
      return;
    }
    if (mode === 'outline') {
      this.refreshOutlineFromMap();
      this.flashMapService.resetZoom();
      this.flashMapService.centerNode();
    } else {
      this.applyOutlineToMap();
      queueMicrotask(() => {
        this.flashMapService.resize();
        this.flashMapService.getContainer()?.focus();
      });
    }
    this.mode.set(mode);
  }

  onOutlineTextChange(text: string): void {
    this.outlineText.set(text);
    this.scheduleOutlineApply();
  }

  onOutlineLineSelect(lineIndex: number): void {
    const nodeId = this.outlineLineToNodeId[lineIndex];
    if (nodeId) {
      this.selectedOutlineLine.set(nodeId);
      this.flashMapService.selectNode(nodeId);
    }
  }

  onOutlineEditFinished(): void {
    this.editingOutlineNodeId.set(null);
  }

  onOutlineNodeSelect(nodeId: string): void {
    this.flashMapService.selectNode(nodeId);
    this.selectedOutlineLine.set(nodeId);
  }

  onOutlineNodeEdit(data: { id: string; topic: string }): void {
    if (!this.editable()) {
      return;
    }
    this.flashMapService.updateNode(data.id, data.topic);
    this.scheduleOutlineRefresh();
  }

  onOutlineNodeCreate(data: { parentId: string; topic: string }): void {
    if (!this.editable()) {
      return;
    }
    const newId = this.createOutlineNodeId();
    this.flashMapService.addNode(data.parentId, newId, data.topic);
    // Selecionar e marcar para edicao o novo no ANTES do refresh
    this.selectedOutlineLine.set(newId);
    this.editingOutlineNodeId.set(newId);
    // Delay para garantir que o no foi adicionado antes de refrescar
    setTimeout(() => {
      this.scheduleOutlineRefresh();
    }, 100);
  }

  onOutlineNodeDelete(nodeId: string): void {
    if (!this.editable()) {
      return;
    }
    this.flashMapService.removeNode(nodeId);
    this.selectedOutlineLine.set(null);
    this.scheduleOutlineRefresh();
  }

  onOutlineNodeIndent(nodeId: string): void {
    if (!this.editable()) {
      return;
    }

    // Encontrar o no anterior no mesmo nivel
    const node = this.flashMapService.getNode(nodeId);
    if (!node || !node.parent) {
      return;
    }

    const parentNode = node.parent;
    if (!parentNode || !parentNode.children) {
      return;
    }

    const siblings = parentNode.children;
    const currentIndex = siblings.findIndex(child => child.id === nodeId);

    if (currentIndex <= 0) {
      // Nao ha irmao anterior
      return;
    }

    // O novo pai e o irmao anterior
    const previousSibling = siblings[currentIndex - 1];
    this.flashMapService.moveNode(nodeId, null, previousSibling.id);
    this.scheduleOutlineRefresh();
  }

  onOutlineNodeOutdent(nodeId: string): void {
    if (!this.editable()) {
      return;
    }

    const node = this.flashMapService.getNode(nodeId);
    if (!node || !node.parent) {
      // Nao ha pai, ja esta no nivel root
      return;
    }

    const currentParent = node.parent;
    if (!currentParent.parent) {
      // O pai e o root, nao pode subir mais
      return;
    }

    // O novo pai sera o avo (pai do pai)
    const grandParent = currentParent.parent;
    this.flashMapService.moveNode(nodeId, null, grandParent.id);
    this.scheduleOutlineRefresh();
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.outlineEnabled()) {
      return;
    }
    if (event.altKey && event.shiftKey && (event.key === 'O' || event.key === 'o')) {
      event.preventDefault();
      this.toggleMode();
    }
  }

  refreshOutlineFromMap(): void {
    const data = this.flashMapService.getData('node_tree') as MindMapData;
    const outline = toOutlineDocument(data);
    this.outlineText.set(outline.text);
    this.outlineLineToNodeId = outline.lineToNodeId;
    this.outlineNodeIdToLine = outline.nodeIdToLine;

    const treeNodes = toOutlineTreeNodes(data);
    this.outlineTreeNodesInternal.set(treeNodes);

    this.syncOutlineSelectionFromMap();
  }

  scheduleOutlineRefresh(): void {
    if (this.outlineRefreshTimer) {
      globalThis.clearTimeout(this.outlineRefreshTimer);
    }
    this.outlineRefreshTimer = globalThis.setTimeout(() => {
      if (this.outlineEnabled()) {
        this.refreshOutlineFromMap();
      }
    }, 200);
  }

  scheduleOutlineApply(): void {
    if (this.outlineApplyTimer) {
      globalThis.clearTimeout(this.outlineApplyTimer);
    }
    this.outlineApplyTimer = globalThis.setTimeout(() => this.applyOutlineToMap(), 300);
  }

  applyOutlineToMap(): void {
    if (!this.flashMapService.isReady()) {
      return;
    }
    const currentMeta = this.getCurrentMeta();
    const { mindData, error, lineToNodeId, nodeIdToLine } = fromOutlineText(this.outlineText(), currentMeta);
    if (error || !mindData) {
      this.outlineError.set(error ?? { line: 1, code: 'invalidOutline' });
      return;
    }

    this.outlineError.set(null);

    const currentData = this.flashMapService.getData('node_tree') as MindMapData;
    const existingLineToNodeId = this.outlineLineToNodeId;
    const extraById: Record<string, Record<string, unknown>> = {};
    const preservedById: Record<string, { direction?: unknown; expanded?: unknown }> = {};
    const collectExtras = (node: Record<string, unknown>): void => {
      const { id, children, direction, expanded, ...extras } = node;
      if (typeof id === 'string') {
        extraById[id] = extras;
        preservedById[id] = { direction, expanded };
      }
      (children as Array<Record<string, unknown>> | undefined)?.forEach(child => collectExtras(child));
    };
    if (currentData && currentData.format === 'node_tree' && currentData.data) {
      collectExtras(currentData.data as Record<string, unknown>);
    }

    const mergedLineToNodeId: Record<number, string> = {};
    Object.keys(lineToNodeId).forEach(lineKey => {
      const lineIndex = Number(lineKey);
      mergedLineToNodeId[lineIndex] = existingLineToNodeId[lineIndex] ?? lineToNodeId[lineIndex];
    });

    const applyExtrasAndIds = (node: Record<string, unknown>): void => {
      const nodeId = node['id'] as string;
      const lineIndex = nodeIdToLine[nodeId];
      const preservedId = lineIndex !== undefined ? existingLineToNodeId[lineIndex] : undefined;
      const targetId = preservedId ?? nodeId;
      if (preservedId) {
        node['id'] = preservedId;
      }
      const extras = extraById[targetId];
      const preserved = preservedById[targetId];
      if (extras) {
        Object.keys(extras).forEach(key => {
          node[key] = extras[key];
        });
      }
      if (preserved) {
        if (preserved.direction !== undefined) {
          node['direction'] = preserved.direction;
        }
        if (preserved.expanded !== undefined) {
          node['expanded'] = preserved.expanded;
        }
      }
      (node['children'] as Array<Record<string, unknown>> | undefined)?.forEach(child => applyExtrasAndIds(child));
    };
    if (mindData && mindData.data) {
      applyExtrasAndIds(mindData.data as Record<string, unknown>);
    }

    const mergedNodeIdToLine: Record<string, number> = {};
    Object.keys(mergedLineToNodeId).forEach(lineKey => {
      const lineIndex = Number(lineKey);
      mergedNodeIdToLine[mergedLineToNodeId[lineIndex]] = lineIndex;
    });

    this.outlineLineToNodeId = mergedLineToNodeId;
    this.outlineNodeIdToLine = mergedNodeIdToLine;

    const zoom = this.flashMapService.getZoom();
    const selectedNode = this.flashMapService.getSelectedNode();
    this.flashMapService.show(mindData, true);
    this.flashMapService.setZoom(zoom);
    if (selectedNode?.id) {
      this.flashMapService.selectNode(selectedNode.id);
    }
  }

  syncOutlineSelectionFromMap(): void {
    const selectedNode = this.flashMapService.getSelectedNode();
    if (!selectedNode) {
      return;
    }
    this.selectedOutlineLine.set(selectedNode.id);
  }

  destroy(): void {
    if (this.outlineApplyTimer) {
      globalThis.clearTimeout(this.outlineApplyTimer);
    }
    if (this.outlineRefreshTimer) {
      globalThis.clearTimeout(this.outlineRefreshTimer);
    }
  }

  private createOutlineNodeId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return `${NODE_ID_PREFIX}${cryptoApi.randomUUID()}`;
    }
    const fallback = Math.random().toString(36).slice(2, 10);
    return `${NODE_ID_PREFIX}${fallback}`;
  }
}

